import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  limit,
} from 'firebase/firestore';
import { getFirestoreDb } from '@/services/firebase';
import {
  getUnreadCount,
  markAllAsRead,
  markAsRead,
  NOTIFICATION_LIST_LIMIT,
  NOTIFICATION_POLL_INTERVAL_MS,
  NOTIFICATIONS_COLLECTION,
} from '@/services/notifications';
import type { Notification, NotificationFilters } from '@/types/notification';

function mapDoc(
  id: string,
  data: Record<string, unknown>,
): Notification {
  const base: Notification = {
    id,
    userId: String(data.userId ?? ''),
    type: data.type as Notification['type'],
    title: String(data.title ?? ''),
    message: String(data.message ?? ''),
    read: data.read === true,
    priority: (data.priority as Notification['priority']) || 'medium',
    createdAt: String(data.createdAt ?? ''),
  };
  if (data.entityType) base.entityType = String(data.entityType);
  if (data.entityId) base.entityId = String(data.entityId);
  if (data.metadata && typeof data.metadata === 'object') {
    base.metadata = data.metadata as Record<string, unknown>;
  }
  if (data.readAt) base.readAt = String(data.readAt);
  return base;
}

export function useNotifications(userId: string | undefined) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshUnread = useCallback(async () => {
    if (!userId) {
      setUnreadCount(0);
      return;
    }
    try {
      setUnreadCount(await getUnreadCount(userId));
    } catch {
      /* ignore */
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(getFirestoreDb(), NOTIFICATIONS_COLLECTION),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(NOTIFICATION_LIST_LIMIT),
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map((d) => mapDoc(d.id, d.data()));
        setNotifications(items);
        setUnreadCount(items.filter((n) => !n.read).length);
        setLoading(false);
      },
      (err) => {
        console.error('[notifications] listener', err);
        setError('No se pudieron cargar las notificaciones.');
        setLoading(false);
      },
    );

    const pollId = window.setInterval(() => {
      void refreshUnread();
    }, NOTIFICATION_POLL_INTERVAL_MS);

    return () => {
      unsubscribe();
      window.clearInterval(pollId);
    };
  }, [userId, refreshUnread]);

  const markRead = useCallback(
    async (notificationId: string) => {
      if (!userId) return;
      await markAsRead(notificationId, userId);
    },
    [userId],
  );

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    await markAllAsRead(userId);
  }, [userId]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markRead,
    markAllRead,
    refreshUnread,
  };
}

export function filterNotifications(
  items: Notification[],
  filters: NotificationFilters,
): Notification[] {
  let out = items;
  if (filters.read !== undefined) {
    out = out.filter((n) => n.read === filters.read);
  }
  if (filters.type) {
    out = out.filter((n) => n.type === filters.type);
  }
  if (filters.search?.trim()) {
    const q = filters.search.trim().toLowerCase();
    out = out.filter(
      (n) =>
        n.title.toLowerCase().includes(q) ||
        n.message.toLowerCase().includes(q),
    );
  }
  return out;
}
